import React, { memo, useState } from 'react';
import { type Site, type ThreatProfile, ZONE_TYPES } from '../api/types.js';
import { CountryNames } from '../utils/Countries.js';
import { getLanguageName } from '../utils/Languages.js';
import { useTranslation } from '../i18n/useTranslation.js';

interface LocationCardProps {
  loc: Site;
  selected: boolean;
  onClick: () => void;
  onEdit?: () => void;
  /** When given, the card offers a delete action (two clicks: arm, then confirm). */
  onDelete?: () => void;
}

const LocationCard = memo(function LocationCard({ loc, selected, onClick, onEdit, onDelete }: LocationCardProps) {
  const { t } = useTranslation();
  const bg = loc.status === 'alert' ? 'r' : loc.status === 'watch' ? 'y' : 'g';
  const lbl =
    loc.status === 'alert'
      ? t('suspectPanel.statusAlert')
      : loc.status === 'watch'
      ? t('suspectPanel.statusWatch')
      : t('suspectPanel.statusClear');

  const icon = ZONE_TYPES.find((t) => t.value === loc.type)?.icon;

  return (
    <div className={`lc ${loc.status === 'alert' ? 'alrt' : ''} ${selected ? 'sel' : ''}`} onClick={onClick}>
      <div className="lrow">
        <div className={`lic ${loc.type}`}>{icon}</div>
        <div className="lin">
          <div className="lnm">{loc.name}</div>
          <div className="lad">{loc.addr}</div>
        </div>
        <span className={`lbg ${bg}`}>{lbl}</span>
      </div>
      <div className="lmt">
        <span className="lst">
          👁 <span className="lstv">{loc.suspects}</span> {t('suspectPanel.suspects')}
        </span>
        <span className="lst">
          📡 <span className="lstv">{t('suspectPanel.active')}</span>
        </span>
        {(onEdit || onDelete) && (
          <span className="loc-actions">
            {onEdit && <EditAction title={t('suspectPanel.editSite')} onEdit={onEdit} />}
            {onDelete && <DeleteAction title={t('suspectPanel.deleteSite')} onConfirm={onDelete} />}
          </span>
        )}
      </div>
    </div>
  );
});

interface ProfileCardProps {
  profile: ThreatProfile;
  onEdit?: () => void;
  /** When given, the card offers a delete action (two clicks: arm, then confirm). */
  onDelete?: () => void;
}

/** Pencil icon; shared by the zone and profile cards. */
function EditAction({ title, onEdit }: { title: string; onEdit: () => void }) {
  return (
    <button
      type="button"
      className="loc-del edit"
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        onEdit();
      }}
    >
      ✎
    </button>
  );
}

/** Trash icon that arms an inline confirm; shared by the zone and profile cards. */
function DeleteAction({ title, onConfirm }: { title: string; onConfirm: () => void }) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  return (
    <span className="loc-del-wrap" onClick={(e) => e.stopPropagation()}>
      {confirming ? (
        <>
          <span className="loc-del-q">{t('suspectPanel.deleteConfirm')}</span>
          <button
            type="button"
            className="loc-del yes"
            title={t('common.yes')}
            onClick={() => {
              setConfirming(false);
              onConfirm();
            }}
          >
            ✓
          </button>
          <button type="button" className="loc-del" title={t('common.no')} onClick={() => setConfirming(false)}>
            ✕
          </button>
        </>
      ) : (
        <button type="button" className="loc-del" title={title} onClick={() => setConfirming(true)}>
          🗑
        </button>
      )}
    </span>
  );
}

const TAG_ICONS = {
  lang: '💬',
  apps: '📱',
  countries: '🌍',
};

const ProfileCard = memo(function ProfileCard({ profile, onEdit, onDelete }: ProfileCardProps) {
  const { t } = useTranslation();
  const tags: React.ReactNode[] = [];

  if (profile.hostileProps.languages.length) {
    profile.hostileProps.languages.forEach((lang) => {
      tags.push(
        <span key={`${profile.id}_lang_${lang}`} className="tpc-tag">
          {`${TAG_ICONS['lang']} ${getLanguageName(lang)} (${lang.toUpperCase()})`}
        </span>
      );
    });
  }
  if (profile.hostileProps.countries.length) {
    profile.hostileProps.countries.forEach((cc2) => {
      tags.push(
        <span key={`${profile.id}_cc_${cc2}`} className="tpc-tag">
          {`${TAG_ICONS['countries']} ${CountryNames[cc2]} (${cc2})`}
        </span>
      );
    });
  }
  if (profile.hostileProps.appBundles.length) {
    profile.hostileProps.appBundles.forEach(({ name, bundle }) => {
      const [cleanBundle, os] = bundle.split('__');
      tags.push(
        <span key={`${profile.id}_apps_${bundle}`} className="tpc-tag">
          {`${TAG_ICONS['apps']} ${name || cleanBundle} (${os})`}
        </span>
      );
    });
  }

  return (
    <div className="tpc">
      <div className="tpc-head">
        <span className="tpc-name">{profile.name}</span>
        <span className="tpc-count">{profile.count}</span>
        {(onEdit || onDelete) && (
          <span className="loc-actions">
            {onEdit && <EditAction title={t('suspectPanel.editProfile')} onEdit={onEdit} />}
            {onDelete && <DeleteAction title={t('suspectPanel.deleteProfile')} onConfirm={onDelete} />}
          </span>
        )}
      </div>
      <div className="tpc-tags">{tags}</div>
    </div>
  );
});

interface SuspectPanelProps {
  protectedSites: Array<Site>;
  threatProfiles: Array<ThreatProfile>;
  selectedLocId?: string;
  onLocClick: (loc: Site) => void;
  /** Zone management actions, present only for roles that may manage zones. */
  onAddSite?: () => void;
  onEditSite?: (loc: Site) => void;
  onDeleteSite?: (loc: Site) => void;
  /** Threat profile actions, present only for roles that may manage threat profiles. */
  onAddProfile?: () => void;
  onEditProfile?: (profile: ThreatProfile) => void;
  onDeleteProfile?: (profile: ThreatProfile) => void;
}

function SuspectPanel({
  protectedSites,
  threatProfiles,
  selectedLocId,
  onLocClick,
  onAddSite,
  onEditSite,
  onDeleteSite,
  onAddProfile,
  onEditProfile,
  onDeleteProfile,
}: SuspectPanelProps) {
  const { t } = useTranslation();
  // Use API profiles if available, otherwise fall back to local constant
  const profiles = threatProfiles;

  return (
    <div className="panel panel-split">
      <div className="panel-section loc-section">
        <div className="ph">
          <div className="ph-top">
            <span className="ph-title">{t('suspectPanel.protectedZones')}</span>
            <span className="ph-count">{protectedSites?.length || 0}</span>
            {onAddSite && (
              <div className="sadd" title={t('suspectPanel.addZoneTitle')} onClick={onAddSite}>
                +
              </div>
            )}
          </div>
        </div>
        <div className="pb loc-list">
          {protectedSites?.map((loc) => (
            <LocationCard
              key={loc.id}
              loc={loc}
              selected={selectedLocId === loc.id}
              onClick={() => onLocClick(loc)}
              onEdit={onEditSite ? () => onEditSite(loc) : undefined}
              onDelete={onDeleteSite ? () => onDeleteSite(loc) : undefined}
            />
          ))}
        </div>
      </div>
      <div className="panel-section profile-section">
        <div className="ph">
          <div className="ph-top">
            <span className="ph-title">{t('suspectPanel.threatProfiles')}</span>
            {onAddProfile && (
              <div className="sadd" title={t('suspectPanel.addProfileTitle')} onClick={onAddProfile}>
                +
              </div>
            )}
          </div>
        </div>
        <div className="pb">
          {profiles.map((p) => (
            <ProfileCard
              key={p.id}
              profile={p}
              onEdit={onEditProfile ? () => onEditProfile(p) : undefined}
              onDelete={onDeleteProfile ? () => onDeleteProfile(p) : undefined}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default SuspectPanel;
